//@flow
import * as React from 'react';
import {
    Columns,
    Column,
    Heading,
    Tabs,
    Tab,
    TabText,
    Box,
    Button,
    Statistic,
    Stack,
    Text
} from '@locus-taxy/neo-ui';
import { DivLoader } from 'common/components/core/wrappers/Div';
import { type ThemeType } from 'react-css-themr';
import { useTranslation } from 'react-i18next';

type PropsTypes = {
    activeTabIndex: number,
    onTabsChange: (Event, string | number) => void,
    existingEntitiesCount: number,
    newEntitiesCount: number,
    theme: ThemeType,
    selectedRowCount: number,
    removedRowsCount: number,
    onNext: () => void,
    onPrev?: () => void,
    onCancel: () => void,
    existingTabTitle: string,
    newTabTitle: string,
    newDataLoading: boolean,
    existingDataLoading: boolean,
    disableNext: boolean
};

const IncrementalPjpNavigationBar = (props: PropsTypes): React.Node => {
    const { t } = useTranslation('entityManagement');
    return (
        <Box p={4} pt={2} pb="6px" width="100%">
            <Columns alignY="center">
                <Column>
                    <Columns alignY="center">
                        <Heading
                            color="grey.700"
                            pr="12px"
                            mr="12px"
                            lineHeight="32px"
                            sx={{
                                borderRight: '1px solid',
                                borderColor: 'grey.300'
                            }}
                        >
                            {t('pjpCreate.incrementalPlan.incrementalPlan')}
                        </Heading>
                        <Box>
                            <Tabs
                                value={props.activeTabIndex}
                                onChange={props.onTabsChange}
                                appearance="card"
                            >
                                <Tab value={0}>
                                    <Stack space="tiny">
                                        <TabText fontSize={1}>
                                            {props.existingTabTitle}
                                        </TabText>
                                        <TabText fontSize={2}>
                                            {props.existingDataLoading ? (
                                                <DivLoader
                                                    placeholderLoaderClassName={
                                                        props.theme
                                                            .countPlaceHolder
                                                    }
                                                    loading
                                                    theme={props.theme}
                                                />
                                            ) : (
                                                props.existingEntitiesCount
                                            )}
                                        </TabText>
                                    </Stack>
                                </Tab>
                                <Tab value={1}>
                                    <Stack space="tiny">
                                        <TabText fontSize={1}>
                                            {props.newTabTitle}
                                        </TabText>
                                        <TabText fontSize={2}>
                                            {props.newDataLoading ||
                                            props.existingDataLoading ? (
                                                <DivLoader
                                                    placeholderLoaderClassName={
                                                        props.theme
                                                            .countPlaceHolder
                                                    }
                                                    loading
                                                    theme={props.theme}
                                                />
                                            ) : (
                                                props.newEntitiesCount
                                            )}
                                        </TabText>
                                    </Stack>
                                </Tab>
                            </Tabs>
                        </Box>
                    </Columns>
                </Column>
                <Columns space="xSmall" alignY="center">
                    <Box pr="2">
                        <Statistic
                            value={props.selectedRowCount}
                            totalValue={props.newEntitiesCount || '-'}
                        />
                        <Text>{t('translations:added')}</Text>
                    </Box>

                    <Box>
                        <Statistic
                            value={props.removedRowsCount}
                            totalValue={props.existingEntitiesCount || '-'}
                        />
                        <Text>{t('pjpCreate.incrementalPlan.removed')}</Text>
                    </Box>

                    <Box
                        pl="12px"
                        ml="2"
                        sx={{
                            borderLeft: '1px solid',
                            borderColor: 'grey.300'
                        }}
                    >
                        <Button
                            palette="primary"
                            appearance="outline"
                            onClick={props.onCancel}
                        >
                            {t('translations:cancel')}
                        </Button>
                    </Box>
                    {props.onPrev && (
                        <Button
                            appearance="solid"
                            palette="primary"
                            onClick={props.onPrev}
                        >
                            {t('translations:back')}
                        </Button>
                    )}
                    <Button
                        appearance="solid"
                        palette="primary"
                        onClick={props.onNext}
                        disabled={props.disableNext}
                    >
                        {t('translations:next')}
                    </Button>
                </Columns>
            </Columns>
        </Box>
    );
};

export default IncrementalPjpNavigationBar;
